import { apiService } from '../api/ApiService';

const API_BASE = '/api/public/game-configurations';

export type GameParameters = {
    rtp?: string | null;
    volatility?: number | null;
    grid?: string | null;
    mechanic?: string | null;
    genres?: string | null;
    maxMultiplier?: string | null;
    category?: string | null;
    releaseDate?: string | null;
    maxWin?: string | null;
    bet?: string | null;
    betMin?: string | null;
    betMax?: string | null;
    reels?: string | null;
    rows?: string | null;
    lines?: string | null;
    mobile?: string | null;
    supportedPlatforms?: string[] | null;
    supportedBrowsers?: string[] | null;
};

type GameConfig = {
    name?: string;
    description?: string;
    parameters?: GameParameters;
};

type ApiGameParameters = GameParameters & {
    name?: string | null;
    description?: string | null;
};

type ApiGame = {
    id: string;
    order: number;
    comingSoon?: boolean;
    comingSoonTitle?: string | null;
    comingSoonBrief?: string | null;
    config?: GameConfig | null;
    parameters?: ApiGameParameters | null;
    logoImagePath?: string | null;
    backgroundImagePath?: string | null;
    version?: string | null;
    cheatsAllowed?: boolean;
    releaseDate?: string | null;
    maxWin?: number | null;
    isMobile?: boolean | null;
    supportedPlatforms?: string[] | null;
    supportedBrowsers?: string[] | null;
    reels?: number | null;
    rows?: number | null;
    gameFeatureFirstCardPath?: string | null;
    gameFeatureSecondCardPath?: string | null;
    gameFeatureThirdCardPath?: string | null;
};

export type Game = {
    id: string;
    name: string;
    order: number;
    description: string;
    comingSoon: boolean;
    comingSoonTitle: string | null;
    comingSoonBrief: string | null;
    logoImagePath: string | null;
    backgroundImagePath: string | null;
    version: string | null;
    cheatsAllowed: boolean;
    gameFeatureFirstCardPath: string | null;
    gameFeatureSecondCardPath: string | null;
    gameFeatureThirdCardPath: string | null;
    parameters: GameParameters;
};

type PublicGamesResponse = {
    games: ApiGame[];
};

type PublicGameResponse = {
    game: ApiGame;
};

export type PreviewPublishSnapshot = {
    id: string;
    updatedAt: string;
    deletedAt?: string | null;
};

export type PreviewPublishDiff = {
    draftGameConfigurations: PreviewPublishSnapshot[];
    gameConfigurations: PreviewPublishSnapshot[];
    draftGameLanguages: PreviewPublishSnapshot[];
    gameLanguages: PreviewPublishSnapshot[];
};

function formatGameName(gameId: string): string {
    return gameId
        .replace(/^game_/, '')
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function isOptionalString(value: unknown): boolean {
    return value === undefined || value === null || typeof value === 'string';
}

function isOptionalNumber(value: unknown): boolean {
    return value === undefined || value === null || typeof value === 'number';
}

function isOptionalBoolean(value: unknown): boolean {
    return value === undefined || value === null || typeof value === 'boolean';
}

function isOptionalStringArray(value: unknown): boolean {
    return (
        value === undefined ||
        value === null ||
        (Array.isArray(value) && value.every((entry) => typeof entry === 'string'))
    );
}

function isApiGameEntry(entry: unknown): entry is ApiGame {
    if (!entry || typeof entry !== 'object') {
        return false;
    }

    const game = entry as Record<string, unknown>;

    return (
        typeof game.id === 'string' &&
        typeof game.order === 'number' &&
        (game.comingSoon === undefined || typeof game.comingSoon === 'boolean') &&
        isOptionalString(game.comingSoonTitle) &&
        isOptionalString(game.comingSoonBrief) &&
        (game.config === null || game.config === undefined || typeof game.config === 'object') &&
        isOptionalString(game.logoImagePath) &&
        isOptionalString(game.backgroundImagePath) &&
        isOptionalString(game.version) &&
        (game.cheatsAllowed === undefined || typeof game.cheatsAllowed === 'boolean') &&
        isOptionalString(game.releaseDate) &&
        isOptionalNumber(game.maxWin) &&
        isOptionalBoolean(game.isMobile) &&
        isOptionalStringArray(game.supportedPlatforms) &&
        isOptionalStringArray(game.supportedBrowsers) &&
        isOptionalNumber(game.reels) &&
        isOptionalNumber(game.rows) &&
        isOptionalString(game.gameFeatureFirstCardPath) &&
        isOptionalString(game.gameFeatureSecondCardPath) &&
        isOptionalString(game.gameFeatureThirdCardPath)
    );
}

const GAME_LOGO_PATHS = [
    'assets/ui/infoPage/GameLogo.png',
    'assets/art/Shared/GameLogo/Logo.png',
    'logo.svg',
    'logo.png',
];

const BACKGROUND_IMAGE_PATHS = [
    'assets/art/Shared/BackgroundWithTransitions/Background_landscape_BaseGame.jpg',
    'assets/art/Shared/Background/Background_landscape_BaseGame.jpg',
    'assets/art/Shared/Background/Background_landscape_FreeGame.jpg',
    'assets/art/Shared/Background/Background_portrait_BaseGame.jpg',
    'assets/art/Shared/Background/Background_portrait_FreeGame.jpg',
    'assets/art/Shared/Background/Background.png',
    'assets/art/Shared/Background/Background.p_2.png',
    'background.jpg',
    'background.png',
    'background.webp',
    'assets/background.jpg',
    'assets/background.png',
    'assets/background.webp',
];

function gameClientPath(gameId: string, relativePath: string): string {
    return `/games/${gameId}/client/dist/${relativePath}`;
}

const resourceExistsCache = new Map<string, boolean>();

async function checkResource(url: string): Promise<boolean> {
    const cached = resourceExistsCache.get(url);
    if (cached !== undefined) {
        return cached;
    }

    try {
        const response = await fetch(url, { method: 'HEAD', cache: 'no-cache' });
        const exists = response.ok;
        resourceExistsCache.set(url, exists);
        return exists;
    } catch {
        resourceExistsCache.set(url, false);
        return false;
    }
}

async function findImageFromClientDist(gameId: string, candidatePaths: string[]): Promise<string | null> {
    for (const candidate of candidatePaths) {
        const fullPath = gameClientPath(gameId, candidate);
        if (await checkResource(fullPath)) {
            return fullPath;
        }
    }
    return null;
}

function formatOptionalNumber(value: number | null | undefined): string | null {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return null;
    }

    return String(value);
}

function formatMobile(value: boolean | null | undefined): string | null {
    if (value === null || value === undefined) {
        return null;
    }

    return value ? 'Yes' : 'No';
}

function formatReleaseDate(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    if (!trimmed) {
        return null;
    }

    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime())) {
        return trimmed;
    }

    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
}

function normalizeLabelList(values: string[] | null | undefined): string[] | null {
    if (!values?.length) {
        return null;
    }

    const labels = values.map((value) => value.trim()).filter(Boolean);
    return labels.length ? labels : null;
}

function firstNonEmptyString(
    ...values: Array<string | null | undefined>
): string | null {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
    }
    return null;
}

function mapApiGameToGame(apiGame: ApiGame): Game {
    const config = apiGame.config || {};
    const legacyParameters = config.parameters || {};
    const parameters = apiGame.parameters || {};

    return {
        id: apiGame.id,
        name:
            firstNonEmptyString(parameters.name, config.name) ||
            formatGameName(apiGame.id),
        order: apiGame.order,
        description:
            firstNonEmptyString(parameters.description, config.description) || '',
        comingSoon: Boolean(apiGame.comingSoon),
        comingSoonTitle: apiGame.comingSoonTitle || null,
        comingSoonBrief: apiGame.comingSoonBrief || null,
        logoImagePath: apiGame.logoImagePath || null,
        backgroundImagePath: apiGame.backgroundImagePath || null,
        version: apiGame.version || null,
        cheatsAllowed: Boolean(apiGame.cheatsAllowed),
        gameFeatureFirstCardPath: apiGame.gameFeatureFirstCardPath?.trim() || null,
        gameFeatureSecondCardPath: apiGame.gameFeatureSecondCardPath?.trim() || null,
        gameFeatureThirdCardPath: apiGame.gameFeatureThirdCardPath?.trim() || null,
        parameters: {
            rtp: parameters.rtp ?? legacyParameters.rtp ?? null,
            volatility: parameters.volatility ?? legacyParameters.volatility ?? null,
            grid: parameters.grid ?? legacyParameters.grid ?? null,
            mechanic: parameters.mechanic ?? legacyParameters.mechanic ?? null,
            genres: parameters.genres ?? legacyParameters.genres ?? null,
            maxMultiplier:
                parameters.maxMultiplier ?? legacyParameters.maxMultiplier ?? null,
            category: parameters.category ?? legacyParameters.category ?? null,
            bet: parameters.bet ?? legacyParameters.bet ?? null,
            betMin: parameters.betMin ?? legacyParameters.betMin ?? null,
            betMax: parameters.betMax ?? legacyParameters.betMax ?? null,
            lines: parameters.lines ?? legacyParameters.lines ?? null,
            releaseDate:
                formatReleaseDate(apiGame.releaseDate) ||
                legacyParameters.releaseDate ||
                null,
            maxWin: formatOptionalNumber(apiGame.maxWin) || legacyParameters.maxWin || null,
            mobile: formatMobile(apiGame.isMobile) || legacyParameters.mobile || null,
            supportedPlatforms:
                normalizeLabelList(apiGame.supportedPlatforms) ||
                legacyParameters.supportedPlatforms ||
                null,
            supportedBrowsers:
                normalizeLabelList(apiGame.supportedBrowsers) ||
                legacyParameters.supportedBrowsers ||
                null,
            reels: formatOptionalNumber(apiGame.reels) || legacyParameters.reels || null,
            rows: formatOptionalNumber(apiGame.rows) || legacyParameters.rows || null,
        },
    };
}

class GamesService {
    public async getLiveGames(): Promise<Game[]> {
        return this.loadGames(API_BASE);
    }

    public async getDraftGames(): Promise<Game[]> {
        return this.loadGames(`${API_BASE}/preview`);
    }

    public async getPreviewPublishDiff(): Promise<PreviewPublishDiff> {
        return apiService.request<PreviewPublishDiff>(`${API_BASE}/preview/publish-diff`);
    }

    public async publishPreview(): Promise<void> {
        await apiService.request(`${API_BASE}/preview/publish`, {
            method: 'POST',
        });
    }

    public async getLiveGame(gameId: string): Promise<Game> {
        const payload = await apiService.request<PublicGameResponse>(
            `${API_BASE}/${encodeURIComponent(gameId)}`
        );

        if (!payload?.game || !isApiGameEntry(payload.game)) {
            throw new Error('Invalid public game configuration API shape');
        }

        return this.resolveGameMedia(mapApiGameToGame(payload.game));
    }

    private async loadGames(url: string): Promise<Game[]> {
        const payload = await apiService.request<PublicGamesResponse>(url);

        if (!payload || !Array.isArray(payload.games)) {
            throw new Error('Invalid public game configurations API shape');
        }

        const games = payload.games.filter(isApiGameEntry);

        if (games.length !== payload.games.length) {
            throw new Error('Public game configurations API contains malformed game entries');
        }

        const mappedGames = games.sort((a, b) => a.order - b.order).map(mapApiGameToGame);

        return Promise.all(mappedGames.map((game) => this.resolveGameMedia(game)));
    }

    private async resolveGameMedia(game: Game): Promise<Game> {
        const [backgroundImagePath, logoImagePath] = await Promise.all([
            game.backgroundImagePath
                ? Promise.resolve(game.backgroundImagePath)
                : findImageFromClientDist(game.id, BACKGROUND_IMAGE_PATHS),
            game.logoImagePath
                ? Promise.resolve(game.logoImagePath)
                : findImageFromClientDist(game.id, GAME_LOGO_PATHS),
        ]);

        return {
            ...game,
            backgroundImagePath,
            logoImagePath,
        };
    }
}

export const gamesService = new GamesService();
